import { useEffect } from "react";

import { useAuth } from "@/components/AuthProvider";
import { useLaborActiveSessionContext } from "@/components/LaborActiveSessionProvider";
import { laborDomainFromSessionPage } from "@/lib/labor/laborDomainFromPage";
import { handleLaborPageDeparture, shouldEndLaborOnPageHide } from "@/lib/labor/laborDeparture";
import {
  clearLaborDepartureSnapshot,
  writeLaborDepartureSnapshot,
} from "@/lib/labor/laborDepartureSnapshot";
import { getLaborTabId } from "@/lib/labor/laborTabId";
import { registerSgsLauncherExitHandler } from "@/lib/sgsLauncherExit";

/**
 * Clocks out persisted labor when this tab unloads (close tab, quit browser, leave the hub origin)
 * or when the manufacturing tablet launcher command-bar **Exit** notifies Hub before finishing
 * HubActivity (Android WebView often skips `pagehide` on that path).
 * Does not run on mere tab switches — those only hide the document; they do not fire `pagehide`.
 */
export function LaborPageDepartureWatch(): null {
  const { session: authSession } = useAuth();
  const { openSession } = useLaborActiveSessionContext();

  useEffect(() => {
    const accessToken = authSession?.access_token?.trim() ?? "";
    const currentTabId = getLaborTabId();
    const sessionId = openSession?.id ?? 0;
    const sessionTabId = typeof openSession?.tab_id === "string" ? openSession.tab_id.trim() : "";

    if (accessToken === "" || sessionId <= 0 || sessionTabId === "" || sessionTabId !== currentTabId) {
      clearLaborDepartureSnapshot();
      return;
    }

    const page = (openSession?.page ?? "").trim();
    writeLaborDepartureSnapshot({
      sessionId,
      domain: laborDomainFromSessionPage(page),
      tabId: currentTabId,
      accessToken,
    });
  }, [authSession?.access_token, openSession?.id, openSession?.page, openSession?.tab_id]);

  useEffect(() => {
    const endLaborOnUnload = (): void => {
      handleLaborPageDeparture(getLaborTabId());
    };
    const onPageHide = (event: PageTransitionEvent): void => {
      if (!shouldEndLaborOnPageHide(event)) {
        return;
      }
      endLaborOnUnload();
    };
    window.addEventListener("pagehide", onPageHide);
    const unregisterLauncherExit = registerSgsLauncherExitHandler(endLaborOnUnload);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      unregisterLauncherExit();
    };
  }, []);

  return null;
}
